import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useGameSounds from "../../../hooks/useGameSounds";
import useKidVoice from "../../../hooks/useKidVoice";
import useGameContent from "../../../hooks/useGameContent";
import GameShell from "../../../components/Kids/GameShell";
import ProgressBar from "../../../components/Kids/ProgressBar";
import Celebration from "../../../components/Kids/Celebration";
import EmptyState from "../../../components/Kids/EmptyState";
import Skeleton from "../../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

// README game-identity gradient — Drag & Drop · Count & Match (blue)
const DRAG_BG = "radial-gradient(130% 100% at 50% 0%, #5B9DF5 0%, #3B6FD4 55%, #2A54B0 100%)";

const DRAG_DEFAULTS = {
  emoji: "🎮",
  bg: DRAG_BG,
  accent: "#3B6FD4",
  type: "count",                       // "count" or "word"
  instruction: "Tap a number, then tap the matching group!",
};

/* ─── GAME CONTENT ─────────────────────────────────────────── */
const GAMES = {
  "count-and-match": {
    title: "Count & Match",
    emoji: "🔢",
    bg: DRAG_BG,
    accent: "#3B6FD4",
    type: "count",
    instruction: "Tap a number, then tap the matching group! 🔢",
    rounds: [
      { items: ["1","2","3"], targets: [
          { display: "🍎",             count: 1, match: "1" },
          { display: "🌟🌟",           count: 2, match: "2" },
          { display: "🎈🎈🎈",         count: 3, match: "3" },
      ]},
      { items: ["4","5","3"], targets: [
          { display: "🐸🐸🐸🐸",       count: 4, match: "4" },
          { display: "🌈🌈🌈🌈🌈",     count: 5, match: "5" },
          { display: "🍓🍓🍓",         count: 3, match: "3" },
      ]},
      { items: ["6","2","5"], targets: [
          { display: "🦋🦋",           count: 2, match: "2" },
          { display: "🐣🐣🐣🐣🐣",     count: 5, match: "5" },
          { display: "🌻🌻🌻🌻🌻🌻",   count: 6, match: "6" },
      ]},
      { items: ["3","7","4"], targets: [
          { display: "🚀🚀🚀🚀🚀🚀🚀", count: 7, match: "7" },
          { display: "🎁🎁🎁",         count: 3, match: "3" },
          { display: "⭐⭐⭐⭐",       count: 4, match: "4" },
      ]},
    ],
  },
  "word-builder": {
    title: "Word Builder",
    emoji: "🔤",
    bg: "radial-gradient(130% 100% at 50% 0%, #a855f7 0%, #7c3aed 100%)",
    accent: "#7c3aed",
    type: "word",
    instruction: "Drag the letters to spell the word! 🔤",
    rounds: [
      { word: "CAT", hint: "🐱", scrambled: ["C","A","T"] },
      { word: "DOG", hint: "🐶", scrambled: ["D","O","G"] },
      { word: "SUN", hint: "☀️", scrambled: ["S","N","U"] },
      { word: "BUS", hint: "🚌", scrambled: ["B","S","U"] },
      { word: "HEN", hint: "🐔", scrambled: ["E","H","N"] },
      { word: "CUP", hint: "☕", scrambled: ["U","C","P"] },
    ],
  },
  "food-sort": {
    title: "Sort Kitchen",
    emoji: "🧺",
    bg: "radial-gradient(130% 100% at 50% 0%, #ef4444 0%, #16a34a 100%)",
    accent: "#ef4444",
    type: "count",
    instruction: "Sort each item into the correct basket! 🍎🥦",
    rounds: [
      { items: ["🍎 Apple","🥕 Carrot","🍌 Banana"],
        targets: [
          { display: "🍎 Fruit",     count: 1, match: "🍎 Apple" },
          { display: "🥦 Vegetable", count: 2, match: "🥕 Carrot" },
          { display: "🍎 Fruit",     count: 3, match: "🍌 Banana" },
        ]},
      { items: ["🍅 Tomato","🍊 Orange","🥔 Potato"],
        targets: [
          { display: "🥦 Vegetable", count: 1, match: "🍅 Tomato" },
          { display: "🍎 Fruit",     count: 2, match: "🍊 Orange" },
          { display: "🥦 Vegetable", count: 3, match: "🥔 Potato" },
        ]},
      { items: ["🍇 Grapes","🧅 Onion","🍒 Cherry","🌽 Corn"],
        targets: [
          { display: "🍎 Fruit",     count: 1, match: "🍇 Grapes" },
          { display: "🥦 Vegetable", count: 2, match: "🧅 Onion" },
          { display: "🍎 Fruit",     count: 3, match: "🍒 Cherry" },
          { display: "🥦 Vegetable", count: 4, match: "🌽 Corn" },
        ]},
      { items: ["🥝 Kiwi","🌶️ Chilli","🍍 Pineapple","🥦 Broccoli"],
        targets: [
          { display: "🍎 Fruit",     count: 1, match: "🥝 Kiwi" },
          { display: "🥦 Vegetable", count: 2, match: "🌶️ Chilli" },
          { display: "🍎 Fruit",     count: 3, match: "🍍 Pineapple" },
          { display: "🥦 Vegetable", count: 4, match: "🥦 Broccoli" },
        ]},
    ],
  },
  "vehicle-sort": {
    title: "Sort & Park",
    emoji: "🚛",
    bg: "radial-gradient(130% 100% at 50% 0%, #22c55e 0%, #059669 100%)",
    accent: "#22c55e",
    type: "count",
    instruction: "Match each vehicle to its correct zone! 🚗",
    rounds: [
      { items: ["🚗 Car","🚢 Cargo Ship","✈️ Aeroplane"],
        targets: [
          { display: "🛣️ Road",  count: 1, match: "🚗 Car" },
          { display: "🌊 Water", count: 2, match: "🚢 Cargo Ship" },
          { display: "☁️ Air",    count: 3, match: "✈️ Aeroplane" },
        ]},
      { items: ["🚂 Cargo Train","🚑 Ambulance","🛶 Kayak"],
        targets: [
          { display: "🛤️ Rail",  count: 1, match: "🚂 Cargo Train" },
          { display: "🛣️ Road",  count: 2, match: "🚑 Ambulance" },
          { display: "🌊 Water", count: 3, match: "🛶 Kayak" },
        ]},
      { items: ["🚁 Helicopter","🚋 Tram","🚒 Fire Brigade"],
        targets: [
          { display: "☁️ Air",   count: 1, match: "🚁 Helicopter" },
          { display: "🛤️ Rail", count: 2, match: "🚋 Tram" },
          { display: "🛣️ Road", count: 3, match: "🚒 Fire Brigade" },
        ]},
      { items: ["🛳️ Cruise Ship","🚀 Space Shuttle","🚄 Bullet Train","🚜 Tractor"],
        targets: [
          { display: "🌊 Water", count: 1, match: "🛳️ Cruise Ship" },
          { display: "☁️ Air",   count: 2, match: "🚀 Space Shuttle" },
          { display: "🛤️ Rail", count: 3, match: "🚄 Bullet Train" },
          { display: "🛣️ Road", count: 4, match: "🚜 Tractor" },
        ]},
    ],
  },
};

export default function DragDropGame() {
  const { slug }   = useParams();
  const [sp]       = useSearchParams();
  const navigate   = useNavigate();

  const { t }      = useTranslation();

  const gameId  = sp.get("gameId");
  const childId = sp.get("childId") || sessionStorage.getItem("childId");
  const childName = sessionStorage.getItem("childName") || "Friend";

  // Built-in games resolve instantly; admin-created games load their content.
  const { game, resolving, notFound } = useGameContent({
    slug, gameId, hardcoded: GAMES, defaults: DRAG_DEFAULTS,
  });

  const sounds      = useGameSounds();
  const { speak }   = useKidVoice();

  const [round,      setRound]     = useState(0);
  const [score,      setScore]     = useState(0);
  const [phase,      setPhase]     = useState("playing"); // playing | roundWin | complete
  const [reward,     setReward]    = useState(null);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (!gameId || !childId) return;
    axios.post(`${API}/api/kids/games/${childId}/start/${gameId}`, {}, {
      headers: { Authorization: `Bearer ${token()}` }
    }).catch(() => {});
  }, []);

  const rounds   = game?.rounds || [];
  const maxRound = rounds.length;

  const handleRoundComplete = (correct) => {
    if (correct) setScore(s => s + 1);
    if (round + 1 >= maxRound) {
      setTimeout(() => endGame(correct ? score + 1 : score), 800);
    } else {
      setTimeout(() => setRound(r => r + 1), 800);
    }
  };

  const endGame = async (finalScore) => {
    setPhase("complete");
    const elapsed = Math.round((Date.now() - startTime.current) / 1000);
    const pct     = Math.round((finalScore / maxRound) * 100);
    const perfect = pct === 100;
    sounds.playComplete(perfect);
    speak(perfect ? `Amazing work, ${childName}! You got them all!` : `Great job, ${childName}!`);
    if (!gameId || !childId) return;
    try {
      const res = await axios.post(
        `${API}/api/kids/games/${childId}/complete/${gameId}`,
        { score: pct, time_spent_seconds: elapsed },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setReward(res.data?.data);
    } catch {}
  };

  if (resolving) return (
    <GameShell title="Loading…" emoji={DRAG_DEFAULTS.emoji} bg={DRAG_BG} onBack={() => navigate(-1)}>
      <Skeleton className="h-2 w-full mb-4" rounded="9999px" />
      <Skeleton className="h-4 w-2/3 mx-auto mb-6" rounded="8px" />
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <Skeleton className="w-16 h-16" rounded="16px" />
        <Skeleton className="w-16 h-16" rounded="16px" />
        <Skeleton className="w-16 h-16" rounded="16px" />
      </div>
      <div className="space-y-3 w-full max-w-sm mx-auto">
        <Skeleton className="h-16 w-full" rounded="16px" />
        <Skeleton className="h-16 w-full" rounded="16px" />
        <Skeleton className="h-16 w-full" rounded="16px" />
      </div>
    </GameShell>
  );

  if (notFound || !game || !(game.rounds && game.rounds.length)) return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4" style={{ background: DRAG_BG }}>
      <EmptyState
        theme="dark"
        mood="thinking"
        title="Oops — no internet!"
        subtitle="This game couldn't load. Check your connection and try again."
        cta="Retry"
        onCta={() => window.location.reload()}
      />
    </div>
  );

  if (phase === "complete") {
    const pct = Math.round((score / maxRound) * 100);
    return <CompleteScreen game={game} score={score} total={maxRound} pct={pct} reward={reward}
      childName={childName}
      onReplay={() => { setRound(0); setScore(0); setPhase("playing"); setReward(null); startTime.current = Date.now(); }}
      onBack={() => navigate(-1)} />;
  }

  const segmented = rounds.map((_, i) => (i < round ? "done" : i === round ? "active" : "pending"));

  return (
    <GameShell
      title={game.title}
      emoji={game.emoji}
      bg={game.bg || DRAG_BG}
      onBack={() => navigate(-1)}
      right={
        <span className="rounded-full px-3.5 min-h-[36px] inline-flex items-center font-display font-semibold text-[13px] text-white"
          style={{ background: "rgba(0,0,0,.16)" }}>
          ⭐ {score}/{maxRound}
        </span>
      }
    >
      <ProgressBar segmented={segmented} className="mb-4" />

      {/* Instruction */}
      <div className="text-center pb-2">
        <p className="text-white/80 font-body font-bold text-sm">{game.instruction || t("kids.games.dragdrop.instruction")}</p>
      </div>

      {/* Game board */}
      <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center pb-8">
        {game.type === "count"
          ? <CountMatch key={round} round={rounds[round]} onComplete={handleRoundComplete} sounds={sounds} speak={speak} />
          : <WordBuilderRound key={round} round={rounds[round]} onComplete={handleRoundComplete} sounds={sounds} speak={speak} />
        }
      </div>
    </GameShell>
  );
}

/* ── COUNT & MATCH ROUND ──
   Tap-to-place interaction: tap a number to select it, then tap the matching
   group to drop it in. Works on touch (iPad/phone) AND mouse. Native HTML5
   drag is kept as a bonus for desktop, but tap is the primary path because
   drag-and-drop does not fire on iOS Safari. */
function CountMatch({ round, onComplete, sounds, speak }) {
  const [slots,    setSlots]    = useState(round.targets.map(() => null));
  const [selected, setSelected] = useState(null); // number picked via tap (or drag)
  const [usedItems, setUsed]    = useState([]);
  const [feedback, setFeedback] = useState({}); // {slotIdx: 'correct'|'wrong'}

  // Place `value` into a slot and score it.
  const place = (slotIdx, value) => {
    if (value === null || value === undefined || slots[slotIdx]) return;
    const target  = round.targets[slotIdx];
    const correct = value === target.match;

    if (correct) { sounds?.playCorrect(); speak?.("Yes! That's right!"); }
    else         { sounds?.playWrong();   speak?.("Oops, try the next one!"); }

    const newSlots = [...slots];
    newSlots[slotIdx] = value;
    setSlots(newSlots);
    setUsed(u => [...u, value]);
    setSelected(null);
    setFeedback(f => ({ ...f, [slotIdx]: correct ? "correct" : "wrong" }));

    if (newSlots.every(Boolean)) {
      const allCorrect = newSlots.every((val, i) => val === round.targets[i].match);
      setTimeout(() => onComplete(allCorrect), 600);
    }
  };

  // Tap a number: toggle it as the active selection.
  const tapNumber = (item) => {
    if (usedItems.includes(item)) return;
    sounds?.playClick();
    setSelected(s => (s === item ? null : item));
  };

  // Tap a group: drop the selected number here.
  const tapTarget = (slotIdx) => {
    if (selected === null || slots[slotIdx]) return;
    place(slotIdx, selected);
  };

  return (
    <div className="w-full max-w-sm">
      {/* Items — tap to pick (drag also works on desktop) */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {round.items.map((item, i) => {
          const used     = usedItems.includes(item);
          const isPicked = selected === item;
          const isLong   = item.length > 3;
          return (
            <button key={i} type="button"
              onClick={() => tapNumber(item)}
              disabled={used}
              draggable={!used}
              onDragStart={() => setSelected(item)}
              onDragEnd={() => setSelected(null)}
              style={{
                touchAction: "manipulation",
                background: used ? "rgba(255,255,255,.15)" : isPicked ? "#FFE066" : "#fff",
                borderBottom: `4px solid ${used ? "transparent" : isPicked ? "#E9C23A" : "#D9DEE6"}`,
                boxShadow: isPicked ? "0 0 0 4px rgba(255,255,255,.5)" : "none",
                color: used ? "rgba(255,255,255,.3)" : "#2A2620",
              }}
              className={`${isLong ? "px-4 py-3 min-w-[80px]" : "w-16 h-16"} rounded-2xl flex items-center justify-center ${isLong ? "text-base" : "text-3xl"} font-display font-semibold transition-all select-none ${
                used ? "cursor-default" : isPicked ? "scale-110 cursor-pointer" : "shadow-lg active:scale-110 hover:scale-105 cursor-pointer"
              }`}>
              {item}
            </button>
          );
        })}
      </div>

      {/* Groups — tap to drop the picked number */}
      <div className="space-y-3">
        {round.targets.map((target, i) => {
          const fb       = feedback[i];
          const tappable = selected !== null && !slots[i];
          const rowStyle =
            fb === "correct" ? { background: "rgba(55,166,91,.35)", borderColor: "#56D98A", borderStyle: "solid" }
            : fb === "wrong" ? { background: "rgba(229,72,29,.35)", borderColor: "#E5484D", borderStyle: "solid" }
            : slots[i]       ? { background: "rgba(255,255,255,.2)", borderColor: "rgba(255,255,255,.4)", borderStyle: "dashed" }
            : tappable        ? { background: "rgba(255,255,255,.18)", borderColor: "rgba(255,255,255,.9)", borderStyle: "dashed" }
            : { background: "rgba(255,255,255,.1)", borderColor: "rgba(255,255,255,.3)", borderStyle: "dashed" };
          return (
            <button key={i} type="button"
              onClick={() => tapTarget(i)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => place(i, selected)}
              style={{ touchAction: "manipulation", borderWidth: 2, ...rowStyle }}
              className={`w-full text-start rounded-2xl p-4 flex items-center gap-4 transition-all ${
                tappable && !fb ? "animate-pulse cursor-pointer" : fb || slots[i] ? "cursor-default" : "cursor-default"
              }`}>
              {/* Emoji display */}
              <div className="text-xl leading-snug flex-1 text-center tracking-wide">{target.display}</div>

              {/* Drop zone — min-w (not a fixed w-14) so a long admin-authored
                  value ("🍎 Apple", not just a single digit) grows the box
                  instead of overflowing it. */}
              <div className={`min-w-[56px] h-14 px-2 rounded-xl flex items-center justify-center font-display font-semibold ${
                slots[i] ? `bg-white text-ink ${(slots[i] || "").length > 3 ? "text-sm" : "text-2xl"}` : "bg-white/20 text-white/40 text-lg"
              }`}>
                {slots[i] || "?"}
              </div>

              {/* Feedback icon */}
              {fb && <span className="text-2xl">{fb === "correct" ? "✅" : "❌"}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── WORD BUILDER ROUND ── */
function WordBuilderRound({ round, onComplete, sounds, speak }) {
  const { t } = useTranslation();
  const [answer,  setAnswer]  = useState([]);    // array of {letter, srcIdx}
  const [usedIdx, setUsedIdx] = useState([]);
  const [flash,   setFlash]   = useState(null);  // 'correct'|'wrong'

  const [availLetters, setAvail] = useState(() =>
    [...round.scrambled].sort(() => Math.random() - 0.5).map((l, i) => ({ l, id: i }))
  );

  const pickLetter = (item) => {
    if (usedIdx.includes(item.id)) return;
    sounds?.playClick();
    const next = [...answer, item];
    setAnswer(next);
    setUsedIdx(u => [...u, item.id]);

    if (next.length === round.word.length) {
      const built = next.map(x => x.l).join("");
      const ok    = built === round.word;
      if (ok) { sounds?.playCorrect(); speak?.(`${round.word}! Well done!`); }
      else    { sounds?.playWrong();   speak?.("Not quite, let's try again!"); }
      setFlash(ok ? "correct" : "wrong");
      setTimeout(() => {
        if (!ok) { setAnswer([]); setUsedIdx([]); setFlash(null); }
        else onComplete(true);
      }, 700);
    }
  };

  const removeLast = () => {
    if (!answer.length) return;
    const last = answer[answer.length - 1];
    setAnswer(a => a.slice(0, -1));
    setUsedIdx(u => u.filter(i => i !== last.id));
    setFlash(null);
  };

  return (
    <div className="w-full max-w-sm text-center">
      {/* Hint word */}
      <div className="mb-6">
        <div className="text-7xl mb-2">{round.hint}</div>
        <p className="text-white/60 text-sm">Spell this word:</p>
        <div className="flex justify-center gap-3 mt-3">
          {round.word.split("").map((_, i) => {
            const filled = answer[i];
            const style = filled
              ? flash === "correct" ? { background: "#37A65B", borderColor: "#268045", color: "#fff" }
              : flash === "wrong"   ? { background: "#E5484D", borderColor: "#B23A32", color: "#fff" }
              : { background: "#fff", borderColor: "#D9DEE6", color: "#2A2620" }
              : { background: "rgba(255,255,255,.2)", borderColor: "rgba(255,255,255,.3)", color: "rgba(255,255,255,.4)" };
            return (
              <div key={i} style={{ borderBottomWidth: 4, borderBottomStyle: "solid", ...style }}
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-display font-semibold transition-all">
                {filled?.l || "_"}
              </div>
            );
          })}
        </div>
        {flash && (
          <div className={`mt-3 text-2xl font-bold ${flash === "correct" ? "text-green-300" : "text-red-300"}`}>
            {flash === "correct" ? `🌟 ${t("kids.games.dragdrop.correct")}` : `❌ ${t("kids.games.dragdrop.wrong")}`}
          </div>
        )}
      </div>

      {/* Scrambled letters */}
      <div className="flex justify-center gap-3 mb-4">
        {availLetters.map((item) => {
          const used = usedIdx.includes(item.id);
          return (
            <button key={item.id} onClick={() => pickLetter(item)} disabled={used || flash === "correct"}
              style={{
                background: used ? "rgba(255,255,255,.15)" : "#fff",
                borderBottom: `4px solid ${used ? "transparent" : "#D9DEE6"}`,
                color: used ? "rgba(255,255,255,.2)" : "#2A2620",
              }}
              className={`w-14 h-14 rounded-2xl text-2xl font-display font-semibold transition-all active:scale-95 ${
                used ? "cursor-default" : "shadow-lg hover:scale-105 cursor-pointer"
              }`}>
              {item.l}
            </button>
          );
        })}
      </div>

      <button onClick={removeLast} className="text-white/60 hover:text-white font-body font-bold text-sm transition">
        ← Remove last letter
      </button>
    </div>
  );
}

/* ── COMPLETE SCREEN (shared) ── */
function CompleteScreen({ game, score, total, pct, reward, childName, onReplay, onBack }) {
  const { t } = useTranslation();
  const perfect = pct === 100;
  const [levelUpSeen, setLevelUpSeen] = useState(false);
  return (
    <div style={{ minHeight: "100dvh", background: game.bg || DRAG_BG }}
      className="overflow-y-auto flex items-center justify-center p-4">
      <style>{`@keyframes pop2{0%{transform:scale(0)}70%{transform:scale(1.1)}100%{transform:scale(1)}}.pop2{animation:pop2 0.5s cubic-bezier(.34,1.56,.64,1) both}`}</style>

      {reward?.level_up && !levelUpSeen && (
        <Celebration
          variant="level"
          title="Level Up!"
          subtitle={`You're now Level ${reward.level}!`}
          cta="Continue"
          onContinue={() => setLevelUpSeen(true)}
        />
      )}

      <div className="bg-white rounded-card-lg p-8 max-w-sm w-full text-center shadow-sheet pop2">
        <div className="text-7xl mb-3">{perfect ? "🏆" : pct >= 60 ? "🌟" : "💪"}</div>
        <h2 className="text-2xl text-ink mb-1 font-display font-semibold">
          {perfect ? "Perfect!" : pct >= 60 ? "Great job!" : "Keep going!"}
        </h2>
        <p className="text-muted mb-5">{childName} got {score} out of {total} correct</p>

        <div className="relative w-28 h-28 mx-auto mb-5">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={game.accent || "#3B6FD4"} strokeWidth="3"
              strokeDasharray={`${pct} 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-display font-semibold text-ink">{pct}%</span>
          </div>
        </div>

        {reward && (
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[["⭐","XP",`+${reward.xp_earned}`,"yellow"],["🪙","Coins",`+${reward.coins_earned}`,"amber"],["🔥","Streak",`${reward.streak_days}d`,"orange"]].map(([i,l,v,c])=>(
              <div key={l} className={`bg-${c}-50 rounded-xl p-2 text-center`}>
                <div className="text-xl">{i}</div>
                <div className="font-extrabold text-sm">{v}</div>
                <div className="text-xs text-muted">{l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onReplay} className="flex-1 bg-faint/20 hover:bg-faint/30 text-ink font-semibold py-3 rounded-2xl transition font-display">
            🔄 {t("kids.games.dragdrop.playAgain")}
          </button>
          <button onClick={onBack} className="flex-1 text-white font-semibold py-3 rounded-2xl shadow-primary transition font-display"
            style={{ background: game.accent || "#3B6FD4" }}>
            🎮 More
          </button>
        </div>
      </div>
    </div>
  );
}
